import Link from "@/components/common/Link";
import toolsLinks from "@/config/toolsLinks";
import {
    footerAiTools,
    footerFeaturedProducts,
    footerPdfToolsCardLink,
    footerPoweredByClipSnapLink,
} from "client/config/footerLinks";
import { Translate } from "components/common/Translate/Translate";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import translatedRoutedSlugs from "translatedRouteSlugs";
import { Internationalization } from "../../Internationalization/Internationalization";
import { Theme } from "../../Navigation/Navbar/Theme/Theme";
import { ClipSnapLogo, FreeConvertLogo, PhotoJoinerLogo, ProPdfLogo } from "./FeaturedProductLogos";

const ContactUs = dynamic<{ title: string }>(
    import("./ContactUs").then((mod) => mod.ContactUs),
    { ssr: false },
);

export interface LanguageProps {
    url: keyof typeof translatedRoutedSlugs;
    urlSlugVariables?: Record<string, string>;
}

interface FooterLink {
    title: string;
    link: string;
}

const VERSION = `v.${process.env["NEXT_PUBLIC_APPLICATION_VERSION"]}`;

const sections = Object.fromEntries(toolsLinks.map((section) => [section.header, section.links]));

const headerClassName = "mb-[18px] text-lg font-semibold tracking-tight text-black dark:text-white";
const linkClassName =
    "py-1.5 text-base font-normal text-gray-600 transition-colors hover:text-irBlue-400 dark:text-darkSurface-600 dark:hover:text-irBlue-200";
const badgeClassName =
    "rounded-full bg-irBlue-400 px-[9px] py-[3px] text-[10px] font-bold uppercase tracking-wide text-white";
/** Theme / language pickers: white pills; the icon selector skips the chevron (inside .rotate-180). */
const prefPillClassName =
    "inline-flex items-center gap-[7px] rounded-[10px] border border-white bg-white px-3 py-2 text-sm text-black transition-colors hover:border-slate-200 dark:border-darkSurface-100 dark:bg-darkSurface-100 dark:text-white dark:hover:border-darkSurface-400 [&>div]:gap-[7px] [&_.items-center>svg]:h-4 [&_.items-center>svg]:w-4";
/** The PDF Tools / AI Photo Editing highlight cards. */
const cardClassName = "flex flex-col rounded-[10px] bg-white p-5 dark:bg-darkSurface-100 sm:col-span-2 lg:col-span-1";

// Semibold, not medium: Source Sans Pro has no 500 weight.
const CardFooterLink = ({ href, testId }: { href: string; testId: string }) => (
    <>
        <div className="my-3 h-px bg-slate-200 dark:bg-darkSurface-400" />
        <Link
            href={href}
            target="_blank"
            data-testid={testId}
            className="group inline-flex items-center gap-1.5 text-base font-semibold transition-colors hover:text-irBlue-400 dark:hover:text-irBlue-200"
        >
            <Translate keyName="built-with-love-by-imageresizer" />
            <span className="transition-transform group-hover:translate-x-[3px]">→</span>
        </Link>
    </>
);

const FooterLinks = ({ links }: { links: readonly FooterLink[] }) => {
    const { locale } = useRouter();
    return (
        <>
            {links.map((link) => {
                if (link.link === "/contact-us") return <ContactUs key={link.title} title={link.title} />;
                const isExternal = link.link.includes("http");
                const translatedRoute = (translatedRoutedSlugs as any)[link.link]?.[locale ?? "en"] ?? link.link;
                return (
                    <Link
                        key={link.link}
                        href={isExternal ? link.link : translatedRoute}
                        target={isExternal ? "_blank" : "_self"}
                        className={linkClassName}
                    >
                        <Translate keyName={link.title} />
                    </Link>
                );
            })}
        </>
    );
};

export const Footer = ({ url, urlSlugVariables }: LanguageProps) => (
    <footer className="mt-auto bg-gray-100 text-black dark:bg-darkSurface-200 dark:text-white">
        <div className="mx-auto flex max-w-[1180px] flex-col px-6 pb-8 pt-10 lg:px-10">
            {/* tool sections */}
            <div className="grid grid-cols-1 gap-[30px] border-b border-slate-200 pb-[34px] dark:border-darkSurface-400 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.15fr_1.15fr]">
                <div className="flex flex-col">
                    <div className={headerClassName}>
                        <Translate keyName="image-tools" />
                    </div>
                    <FooterLinks links={sections["image-tools"]} />
                </div>
                <div className="flex flex-col">
                    <div className={headerClassName}>
                        <Translate keyName="image-converters" />
                    </div>
                    <FooterLinks links={sections["image-converters"]} />
                </div>
                <div className={cardClassName}>
                    <div className={`${headerClassName} flex items-center gap-[9px]`}>
                        <Translate keyName="pdf-tools" />
                        <span className={badgeClassName}>
                            <Translate keyName="new-badge" />
                        </span>
                    </div>
                    <FooterLinks links={sections["pdf-tools"]} />
                    <CardFooterLink href={footerPdfToolsCardLink} testId="footer-pdf-tools-built-by" />
                </div>
                <div className={cardClassName} data-testid="footer-ai-photo-editing">
                    <div className={`${headerClassName} flex items-center gap-[9px]`}>
                        <Translate keyName="ai-photo-editing" />
                        <span className={badgeClassName}>
                            <Translate keyName="new-badge" />
                        </span>
                    </div>
                    <FooterLinks links={footerAiTools} />
                    <CardFooterLink href={footerPoweredByClipSnapLink} testId="footer-powered-by-clipsnap" />
                </div>
            </div>

            {/* apps / about / featured products */}
            <div className="grid grid-cols-1 gap-8 py-[34px] md:grid-cols-2">
                <div>
                    <div className={headerClassName}>
                        <Translate keyName="apps" />
                    </div>
                    <div className="grid max-w-[440px] grid-cols-1 gap-x-3 sm:grid-cols-2">
                        <FooterLinks links={sections["apps"]} />
                    </div>
                </div>
                <div>
                    <div className={headerClassName}>
                        <Translate keyName="about" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-[26px] gap-y-2 [&>li]:list-none [&>li]:py-1.5 [&>li]:text-base [&>li]:text-gray-600 dark:[&>li]:text-darkSurface-600">
                        <FooterLinks links={sections["about"]} />
                    </div>
                    <div className="mt-[26px] flex flex-col items-start gap-[14px] border-t border-slate-200 pt-[26px] dark:border-darkSurface-400">
                        <span className="text-lg font-semibold">
                            <Translate keyName="featured-products" />
                        </span>
                        <div className="flex flex-wrap items-center gap-7">
                            {footerFeaturedProducts.map((product) => (
                                <Link
                                    key={product.name}
                                    href={product.link}
                                    target="_blank"
                                    aria-label={product.name}
                                    title={product.name}
                                    // The anchor is the hover surface: it must stay still (the lift animation
                                    // lives on the inner span) or edge-hovering jitters, and p/-m enlarge the
                                    // hit area without changing the visual gaps.
                                    className="group -m-2 inline-flex p-2"
                                >
                                    <span className="inline-flex h-6 items-center opacity-80 grayscale transition-all group-hover:-translate-y-px group-hover:opacity-100 group-hover:grayscale-0 dark:brightness-150 dark:group-hover:brightness-100 [&>svg]:h-[22px] [&>svg]:w-auto">
                                        {product.name === "ProPDF" ? (
                                            <ProPdfLogo />
                                        ) : product.name === "ClipSnap" ? (
                                            <ClipSnapLogo />
                                        ) : product.name === "FreeConvert" ? (
                                            <FreeConvertLogo />
                                        ) : (
                                            <PhotoJoinerLogo />
                                        )}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* bottom bar */}
            <div className="mt-2.5 flex flex-col items-start gap-[14px] border-t border-slate-200 pt-[22px] dark:border-darkSurface-400 lg:flex-row lg:items-center lg:gap-[18px]">
                <span className="text-sm text-gray-600 dark:text-darkSurface-600 sm:text-base">
                    {`© ${new Date().getFullYear()} ImageResizer `}
                    <Link
                        href={`/changelogs#${VERSION}`}
                        target="_blank"
                        className="text-irBlue-600 dark:text-irBlue-200"
                    >
                        {VERSION}
                    </Link>
                    {". "}
                    <Translate keyName="all-rights-reserved" />
                    {"."}
                </span>
                <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                    <Theme onlyIconMode={false} className={prefPillClassName} />
                    <Internationalization url={url} urlSlugVariables={urlSlugVariables} className={prefPillClassName} />
                </div>
            </div>
        </div>
    </footer>
);
